'use client';

/**
 * LoyaltyAppPreview — split-screen "what you do at the counter" with
 * the product screenshot on one side and the 5-step workflow on the
 * other.
 *
 * Lifted from Part 15 64.1 ("Optimized for speed: 5–10 seconds per
 * customer"). Frames the app as a transaction tool, not a management
 * console — those live in the web dashboard.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import { HiCheckCircle } from 'react-icons/hi2';

type Props = { lang: Locale };

const LoyaltyAppPreview = ({ lang }: Props) => {
  const { t } = useTranslation(lang);
  const steps = t('LoyaltyApp/Preview/steps', { returnObjects: true }) as string[];

  return (
    <section
      id="preview"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="01" label={t('LoyaltyApp/Preview/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('LoyaltyApp/Preview/title1')} <span style={{ color: '#B4C1D8' }}>{t('LoyaltyApp/Preview/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('LoyaltyApp/Preview/subtitle')}
        </p>

        <div className="mx-auto" style={{ maxWidth: 720 }}>
          <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {steps.map((step, i) => (
              <li
                key={i}
                className="flex items-start gap-4"
                style={{
                  padding: '14px 0',
                  borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div
                  className="mono flex-shrink-0 inline-flex items-center justify-center"
                  style={{
                    width: 32,
                    height: 32,
                    fontSize: 12,
                    letterSpacing: 1,
                    color: '#22D3EE',
                    background: 'rgba(34,211,238,0.10)',
                    border: '1px solid rgba(34,211,238,0.30)',
                    borderRadius: 8,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <span
                  style={{
                    color: '#F5F7FA',
                    fontSize: 16,
                    lineHeight: 1.5,
                    flex: 1,
                  }}
                >
                  {step}
                </span>
              </li>
            ))}
            <li
              className="flex items-center gap-3"
              style={{
                marginTop: 18,
                padding: '14px 18px',
                background: 'rgba(34,211,238,0.06)',
                border: '1px solid rgba(34,211,238,0.30)',
                borderRadius: 12,
              }}
            >
              <HiCheckCircle style={{ width: 18, height: 18, color: '#22D3EE' }} />
              <span style={{ color: '#F5F7FA', fontSize: 14 }}>{t('LoyaltyApp/Preview/total')}</span>
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
};

export default LoyaltyAppPreview;
